import { useState } from "react";
import { usePayWithPoints } from "../usePayments";
import { useToast } from "../../context/ToastContext";

type PayWithPointsButtonProps = {
  itemId?: number;
  userPoints?: number;
  requiredPoints?: number;
  onSuccess?: (data: any) => void;
  className?: string;
};

export default function PayWithPointsButton({
  itemId,
  userPoints,
  requiredPoints,
  onSuccess,
  className = "",
}: PayWithPointsButtonProps) {
  const { mutateAsync, isPending } = usePayWithPoints();
  const toast = useToast();
  const [hasPaid, setHasPaid] = useState(false);

  const insufficient =
    typeof userPoints === "number" &&
    typeof requiredPoints === "number" &&
    userPoints < requiredPoints;

  const handlePay = async () => {
    if (!itemId) return toast.error("Missing ask ID");
    if (insufficient) return toast.error("Insufficient points to complete this payment.");

    try {
      const result = await mutateAsync(itemId);
      toast.success("Payment successful!");
      setHasPaid(true);
      onSuccess?.(result);
    } catch (err: any) {
      toast.error(err.message || "An error occurred while processing payment.");
    }
  };

  const disabled = !itemId || isPending || hasPaid || insufficient;

  return (
    <div className="flex flex-col items-center space-y-2">
      <button
        type="button"
        onClick={handlePay}
        disabled={disabled}
        className={`relative flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl font-semibold text-white shadow-sm transition
          ${
            hasPaid
              ? "bg-green-600 cursor-not-allowed"
              : insufficient
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }
          disabled:opacity-70 disabled:cursor-not-allowed ${className}`}
      >
        {isPending ? (
          <>
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <span>Processing...</span>
          </>
        ) : hasPaid ? (
          "Paid with Points"
        ) : insufficient ? (
          "Insufficient Points"
        ) : (
          "Pay with Points"
        )}
      </button>

      {typeof userPoints === "number" && typeof requiredPoints === "number" && (
        <p
          className={`text-sm font-medium ${
            insufficient ? "text-red-500" : "text-gray-600"
          }`}
        >
          Your Points: {userPoints} / Required: {requiredPoints}
        </p>
      )}

      {insufficient && (
        <p className="text-xs text-red-500 italic">
          You don’t have enough points to complete this payment.
        </p>
      )}
    </div>
  );
}